import * as React from 'react';
import { useState } from 'react';
import styles from './index.less';
import { ConfigProvider, DatePicker, Input } from 'antd';
import dayjs from 'dayjs';
import { FI18n, FServiceAPI, FUtil } from '@freelog/tools-lib';
import enUS from 'antd/locale/en_US';
import zhCN from 'antd/locale/zh_CN';
import FIcons from '@/components/FIcons';
// import FSelect2 from '@/components/FSelect2';
import FInput_PinyinSafeTextCounter from '@/components/FInput_PinyinSafeTextCounter';
import FRectBtn from '@/components/FButton/FRectBtn';
import FComponentsLib from '@freelog/components-lib';
import * as AHooks from 'ahooks';
import fMessage from '@/components/fMessage';
import { connect } from 'dva';
import { ConnectState, NodeManagerModelState } from '@/models/connect';
import { Dispatch } from 'redux';
import FListFooter, { listStateAndListMore } from '@/components/FListFooter';
import FLoadingTip from '@/components/FLoadingTip';
import FNoDataTip from '@/components/FNoDataTip';
import FContractDetailsDrawer2, {
  setState_FContractDetailsDrawer2,
} from '@/components/FContractDetailsDrawer2';
import FTooltip from '@/components/FTooltip';
import FAmountRangeInput from '@/components/FAmountRangeInput';

interface TransactionProps {
  dispatch: Dispatch;
  nodeManagerPage: NodeManagerModelState;
}

interface TransactionStates {
  dataTime: {
    startDateTime: string;
    endDateTime: string;
    selectedTimeRange: 'week' | 'month' | 'year' | '';
  };
  statisticData: {
    totalIncome: number;
    totalExpense: number;
  };
  isFilterExpanded: boolean;
  filter: {
    transactionStatus: 'completed';
    transactionType: 'contract';
    minAmount: string;
    maxAmount: string;
    keywords: string;
  };
  listData: {
    id: string;
    dateTime: string;
    counterpartyId: number;
    counterpartyName: string;
    counterpartyType: 'user' | 'node';
    transactionAmount: number;
    remark: string; // 交易备注
    contractID: string;
    contractName: string;
    transactionStatus: number;
  }[];

  hasFilterCriteria: boolean;
  totalItem: number;
  listState: 'loading' | 'noData' | 'noSearchResult' | 'loaded';
  listMore: 'loading' | 'andMore' | 'noMore';
}

const initStates: TransactionStates = {
  dataTime: {
    // startDateTime: moment().subtract(1, 'month').format(FUtil.Predefined.momentDateFormat),
    startDateTime: dayjs().subtract(29, 'day').format(FUtil.Predefined.momentDateFormat),
    endDateTime: dayjs().format(FUtil.Predefined.momentDateFormat),
    selectedTimeRange: 'month',
  },
  statisticData: {
    totalIncome: 0,
    totalExpense: 0,
  },
  isFilterExpanded: false,
  filter: {
    transactionStatus: 'completed',
    transactionType: 'contract',
    minAmount: '',
    maxAmount: '',
    keywords: '',
  },
  listData: [],

  hasFilterCriteria: false,
  totalItem: 0,
  listState: 'loading',
  listMore: 'loading',
};

function Transaction({ dispatch, nodeManagerPage }: TransactionProps) {
  const [$dataTime, set$dataTime, get$dataTime] = FUtil.Hook.useGetState<
    TransactionStates['dataTime']
  >(initStates['dataTime']);

  const [$statisticData, set$statisticData, get$statisticData] = FUtil.Hook.useGetState<
    TransactionStates['statisticData']
  >(initStates['statisticData']);

  const [$isFilterExpanded, set$isFilterExpanded, get$isFilterExpanded] = FUtil.Hook.useGetState<
    TransactionStates['isFilterExpanded']
  >(initStates['isFilterExpanded']);
  const [$filter, set$filter, get$filter] = FUtil.Hook.useGetState<TransactionStates['filter']>(
    initStates['filter'],
  );
  const [$listData, set$listData, get$listData] = FUtil.Hook.useGetState<
    TransactionStates['listData']
  >(initStates['listData']);

  const [$hasFilterCriteria, set$hasFilterCriteria, get$hasFilterCriteria] = FUtil.Hook.useGetState<
    TransactionStates['hasFilterCriteria']
  >(initStates['hasFilterCriteria']);
  const [$listTotalItem, set$listTotalItem, get$listTotalItem] = FUtil.Hook.useGetState<
    TransactionStates['totalItem']
  >(initStates['totalItem']);
  const [$listMore, set$listMore, get$listMore] = FUtil.Hook.useGetState<
    TransactionStates['listMore']
  >(initStates['listMore']);
  const [$listState, set$listState, get$listState] = FUtil.Hook.useGetState<
    TransactionStates['listState']
  >(initStates['listState']);

  AHooks.useMount(async () => {
    handleStatisticData();
    handleListData();
  });

  async function handleStatisticData() {
    const {
      ret,
      errCode,
      msg,
      data,
    }: {
      ret: number;
      errCode: number;
      msg: string;
      data: {
        depositAmount: number;
        withdrawalAmount: number;
      };
    } = await FServiceAPI.Payment.queryStatistics({
      ownerId: nodeManagerPage.nodeId,
      ownerType: 2,
      startTransactionDate: get$dataTime().startDateTime + ' 00:00:00',
      endTransactionDate: get$dataTime().endDateTime + ' 23:59:59',
    });
    if (ret !== 0 || errCode !== 0) {
      fMessage(msg, 'error');
      return;
    }

    // console.log(data, 'data');
    set$statisticData({
      totalIncome: data.depositAmount,
      totalExpense: data.withdrawalAmount,
    });
  }

  async function handleListData(append: boolean = false) {
    let oldListData: TransactionStates['listData'] = [];
    if (append) {
      oldListData = get$listData();
      set$listMore('loading');
    } else {
      set$listState('loading');
    }

    const filter = get$filter();

    const {
      data,
      ret,
      errCode,
      msg,
    }: {
      ret: number;
      errCode: number;
      msg: string;
      data: {
        totalItem: number;
        dataList: {
          id: string; // 交易ID
          transactionDate: string; // "2026-01-04T07:58:10.749Z"
          transactionStatus: 1; // 交易状态 1:交易完成
          transactionAmount: number; // 交易金额
          transactionType: 1 | 2; // 交易类型 1:入金 2:出金
          remark: string; // 交易备注
          counterpartyName: string; // 交易方名称
          counterpartyId: number; // 交易方ID
          counterpartyType: 1; // 交易方类型 1:用户 2:节点
          attachInfo: {
            contractId: string;
            contractName: string;
          };
        }[];
      };
    } = await FServiceAPI.Payment.queryTransactionList({
      skip: oldListData.length,
      limit: 100,
      ownerId: nodeManagerPage.nodeId,
      ownerType: 2,
      startTransactionDate: get$dataTime().startDateTime + ' 00:00:00',
      endTransactionDate: get$dataTime().endDateTime + ' 23:59:59',
      keywords: encodeURIComponent(filter.keywords),
      transactionStatus: 1,
      businessType: 'ContractRoutinePayment',
      minTransactionAmount: filter.minAmount ? Number(filter.minAmount) : undefined,
      maxTransactionAmount: filter.maxAmount ? Number(filter.maxAmount) : undefined,
    });

    if (ret !== 0 || errCode !== 0) {
      fMessage(msg, 'error');
      return;
    }

    const hasFilterCriteria =
      filter.keywords !== '' || filter.minAmount !== '' || filter.maxAmount !== '';

    const newListData: TransactionStates['listData'] = data.dataList.map((item) => {
      return {
        id: item.id,
        dateTime: dayjs(item.transactionDate).format('YYYY-MM-DD HH:mm:ss'),
        counterpartyId: item.counterpartyId,
        counterpartyName: item.counterpartyName,
        counterpartyType: item.counterpartyType === 1 ? 'user' : 'node',
        transactionAmount:
          item.transactionType === 1 ? -item.transactionAmount : item.transactionAmount,
        remark: item.remark,
        contractID: item.attachInfo.contractId,
        contractName: item.attachInfo.contractName,
        transactionStatus: item.transactionStatus,
      };
    });

    const list: TransactionStates['listData'] = [...oldListData, ...newListData];
    const { state, more } = listStateAndListMore({
      list_Length: list.length,
      total_Length: data.totalItem,
      has_FilterCriteria: hasFilterCriteria,
    });

    set$hasFilterCriteria(hasFilterCriteria);
    set$listState(state);
    set$listMore(more);
    set$listTotalItem(data.totalItem);
    set$listData(list);
  }

  const activeFilterCount = [
    $filter.keywords ? 1 : 0,
    $filter.minAmount || $filter.maxAmount ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  return (
    <div className={styles.container}>
      <div className={styles.headerSection}>
        <div className={styles.title}>
          {
            // 交易记录
            FI18n.i18nNext.tAuto('nodemgnt_transactionshistory_title')
          }
        </div>
        <div className={styles.timeFilter}>
          <ConfigProvider locale={FI18n.i18nNext.getCurrentLanguage() === 'zh_CN' ? zhCN : enUS}>
            <DatePicker.RangePicker
              className={styles.datePicker}
              allowClear={false}
              value={[dayjs($dataTime.startDateTime), dayjs($dataTime.endDateTime)]}
              onChange={(values: any) => {
                set$dataTime({
                  ...get$dataTime(),
                  startDateTime: values[0].format(FUtil.Predefined.momentDateFormat),
                  endDateTime: values[1].format(FUtil.Predefined.momentDateFormat),
                  selectedTimeRange: '',
                });
                handleStatisticData();
                handleListData();
              }}
              disabledDate={(date) => {
                return dayjs().isBefore(date);
              }}
            />
          </ConfigProvider>
          <div className={styles.timeRangeButtons}>
            {[
              {
                label:
                  // '近一周'
                  FI18n.i18nNext.tAuto('transactionhistory_filter_last7days'), value: 'week'
              },
              {
                label:
                  // '近一月'
                  FI18n.i18nNext.tAuto('transactionhistory_filter_last30days')
                , value: 'month'
              },
              {
                label:
                  // '近一年'
                  FI18n.i18nNext.tAuto('transactionhistory_filter_last365days'), value: 'year'
              },
            ].map((range) => (
              <span
                key={range.value}
                className={`${styles.timeRangeButton} ${$dataTime.selectedTimeRange === range.value ? styles.active : ''
                  }`}
                onClick={() => {
                  set$dataTime({
                    ...get$dataTime(),
                    selectedTimeRange: range.value as 'week' | 'month' | 'year',
                    startDateTime: dayjs()
                      .subtract(range.value === 'week' ? 6 : range.value === 'month' ? 29 : 364, 'day')
                      .format(FUtil.Predefined.momentDateFormat),
                    endDateTime: dayjs().format(FUtil.Predefined.momentDateFormat),
                  });
                  handleStatisticData();
                  handleListData();
                }}
              >
                {range.label}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.revenueModule}>
        <div className={styles.revenueCard}>
          <div className={styles.cardHeader}>
            <div className={styles.titleSection}>
              <span className={styles.cardTitle}>
                {
                  // 收入（元）
                  FI18n.i18nNext.tAuto('transactionhistory_income')
                }
              </span>
              {/* <div className={styles.infoIcon}>
                <FIcons.FInfo style={{ fontSize: 16, width: 16, height: 16 }} />
              </div> */}
            </div>
          </div>
          <div className={styles.cardContent}>
            <span className={styles.amount}>{$statisticData.totalIncome.toFixed(2)}</span>
          </div>
        </div>

        <div className={styles.revenueCard}>
          <div className={styles.cardHeader}>
            <div className={styles.titleSection}>
              <span className={styles.cardTitle}>{
                // 支出（元）
                FI18n.i18nNext.tAuto('transactionhistory_expense')
              }</span>
              {/* <div className={styles.infoIcon}>
                <FIcons.FInfo style={{ fontSize: 16, width: 16, height: 16 }} />
              </div> */}
            </div>
          </div>
          <div className={styles.cardContent}>
            <span className={styles.amount}>{$statisticData.totalExpense.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {$listState === 'noData' && (
        <div className={styles.listEmptyWrap}>
          <FNoDataTip
            height={400}
            tipText={
              // 暂无交易记录
              FI18n.i18nNext.tAuto('tranaction_history_list_empty')
            }
          />
        </div>
      )}

      {
        $listState !== 'noData' && (<div className={styles.transactionRecords}>
          <div className={styles.recordsHeader}>
            <span className={styles.recordsTitle}>
              {
                // 交易记录
                FI18n.i18nNext.tAuto('transactionhistory_recent')
              }
            </span>
            <div
              className={`${styles.filterToggle} ${$isFilterExpanded ? styles.filterToggleExpanded : ''}`}
              onClick={() => set$isFilterExpanded(!get$isFilterExpanded())}
            >
              <div className={styles.filterIcon}>
                <FIcons.FFilter style={{ fontSize: 12 }} />
              </div>
              <span className={styles.filterText}>
                {FI18n.i18nNext.tAuto('transactions_btn_showfilter').replace(/^展开|^收起/, '').trim() || '筛选'}
              </span>
              {activeFilterCount > 0 && (
                <span className={styles.filterCountBadge}>{activeFilterCount}</span>
              )}
            </div>
          </div>

          <>
            {$isFilterExpanded && (
              <div className={styles.filterSection}>
                <div className={styles.filterRow}>
                  {/* <div className={styles.filterItem}>
            <div className={styles.filterLabel}>交易状态 transactions_filter_status</div>
            <FSelect2
              options={[{ label: '交易完成', value: 'completed' }]}
              value={$filter.transactionStatus}
              onChange={(value) => {}}
              style={{ width: '100%', height: 38 }}
              placeholder='请选择'
            />
          </div>
          <div className={styles.filterItem}>
            <div className={styles.filterLabel}>交易类型 transactions_filter_type</div>
            <FSelect2
              options={[{ label: '合约交易', value: 'contract' }]}
              value={$filter.transactionType}
              onChange={(value) => {}}
              style={{ width: '100%', height: 38 }}
              placeholder='请选择'
            />
          </div> */}
                  <div className={styles.filterItem}>
                    <div className={styles.filterLabel}>{
                      // 金额
                      FI18n.i18nNext.tAuto('transactions_filter_amount')
                    }</div>
                    <div className={styles.amountRange}>
                      {/* <div className={styles.amountInput}>
                        <Input
                          value={$filter.minAmount}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '') {
                              set$filter({
                                ...get$filter(),
                                minAmount: '',
                              });
                              return;
                            }
                            // 验证：判断 value 是否是大于等于0的数字，最多2位小数（允许输入过程中的小数点）
                            if (!/^(0|[1-9]\d*)(\.\d{0,2})?$/.test(value)) {
                              // fMessage('请输入有效的金额，最多2位小数', 'error');
                              return;
                            }
                            // console.log(value, 'value');

                            set$filter({
                              ...get$filter(),
                              minAmount: value,
                            });
                          }}
                          style={{ width: '100%', height: 38, border: 'none' }}
                          placeholder='最小金额'
                          onBlur={() => {
                            if (
                              get$filter().maxAmount !== '' &&
                              get$filter().minAmount !== '' &&
                              Number(get$filter().minAmount) > Number(get$filter().maxAmount)
                            ) {
                              set$filter({
                                ...get$filter(),
                                minAmount: get$filter().maxAmount,
                              });
                            }
                          }}
                        />
                        <span className={styles.separator}>—</span>
                        <Input
                          value={$filter.maxAmount}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '') {
                              set$filter({
                                ...get$filter(),
                                maxAmount: '',
                              });
                              return;
                            }
                            // 验证：判断 value 是否是大于等于0的数字，最多2位小数（允许输入过程中的小数点）
                            if (!/^(0|[1-9]\d*)(\.\d{0,2})?$/.test(value)) {
                              return;
                            }

                            set$filter({
                              ...get$filter(),
                              maxAmount: value,
                            });
                          }}
                          style={{ width: '100%', height: 38, border: 'none' }}
                          placeholder='最大金额'
                          onBlur={() => {
                            if (
                              get$filter().minAmount !== '' &&
                              get$filter().maxAmount !== '' &&
                              Number(get$filter().minAmount) > Number(get$filter().maxAmount)
                            ) {
                              set$filter({
                                ...get$filter(),
                                maxAmount: get$filter().minAmount,
                              });
                            }
                          }}
                        />
                      </div> */}
                      <FAmountRangeInput
                        minAmount={$filter.minAmount}
                        maxAmount={$filter.maxAmount}
                        onChangeMinAmount={(value) => {
                          set$filter({ ...get$filter(), minAmount: value });
                        }}
                        onChangeMaxAmount={(value) => {
                          set$filter({ ...get$filter(), maxAmount: value });
                        }}
                      />
                    </div>
                  </div>
                  <div className={styles.filterItem}>
                    <div className={styles.filterLabel}>
                      {
                        // 交易ID/合约ID/标的物授权标识
                        FI18n.i18nNext.tAuto('transactions_filter_id')
                      }
                    </div>
                    <FInput_PinyinSafeTextCounter
                      lengthLimit={-1}
                      value={$filter.keywords}
                      onChangeValue={(value) => {
                        set$filter({
                          ...get$filter(),
                          keywords: value,
                        });
                      }}
                      style={{ width: '100%', height: 38, borderRadius: 4 }}
                    />
                  </div>
                  <div className={styles.filterActions}>
                    <FRectBtn
                      type='default'
                      onClick={() => {
                        set$filter({
                          ...get$filter(),
                          transactionStatus: 'completed',
                          transactionType: 'contract',
                          minAmount: '',
                          maxAmount: '',
                          keywords: '',
                        });
                        handleListData();
                      }}
                    >
                      {
                        // 重置
                        FI18n.i18nNext.tAuto('transactions_btn_resetfilter')
                      }
                    </FRectBtn>
                    <FRectBtn type='primary' onClick={() => handleListData()}>
                      {
                        // 筛选
                        FI18n.i18nNext.tAuto('transactions_filter_btn_applyfilter')
                      }
                    </FRectBtn>
                  </div>
                </div>
              </div>
            )}

            <div className={styles.resultsInfo}>
              <span className={styles.resultsCount}>
                {FI18n.i18nNext.tAuto('transactions_msg_resultqty', { qty: $listTotalItem })}
              </span>
            </div>

            {$listState === 'loading' && <FLoadingTip height={600} />}

            {$listState === 'noSearchResult' && (
              <FNoDataTip
                height={600}
                tipText={
                  // 无相关的记录
                  FI18n.i18nNext.t('transactions_empty_no_resault_found')
                }
              />
            )}

            {$listState === 'loaded' && (
              <div className={styles.table}>
                <div className={styles.tableHeader}>
                  <div className={styles.headerCell}>
                    {
                      // 日期
                      FI18n.i18nNext.tAuto('transactions_list_date')
                    }
                  </div>
                  <div className={styles.headerCell}>
                    {
                      // 交易方
                      FI18n.i18nNext.tAuto('transactions_list_toorfrom')
                    }
                  </div>
                  <div className={styles.headerCellInstruction}>
                    {
                      // 交易说明
                      FI18n.i18nNext.tAuto('transactions_list_desc')
                    }
                  </div>
                  <div className={styles.headerCell}>
                    {
                      // 金额
                      FI18n.i18nNext.tAuto('transactions_list_amount')
                    }
                  </div>
                  <div className={styles.headerCellStatus}>
                    {
                      // 交易状态
                      FI18n.i18nNext.tAuto('transactions_list_status')
                    }
                  </div>
                </div>

                <div className={styles.tableBody}>
                  {$listData.map((record, index) => (
                    <React.Fragment key={index}>
                      <div className={styles.rowDivider}></div>
                      <div className={styles.tableRow}>
                        <div className={styles.tableCellDate}>
                          <span className={styles.dateTime}>{record.dateTime}</span>
                        </div>
                        <div className={styles.tableCell}>
                          <div className={styles.traderInfo}>
                            <div className={styles.traderName}>
                              <div className={styles.avatar}>
                                {record.counterpartyType === 'user' && (
                                  <FIcons.FUser style={{ fontSize: 16, color: '#222' }} />
                                )}
                                {record.counterpartyType === 'node' && (
                                  <FIcons.FNodes style={{ fontSize: 16, color: '#222' }} />
                                )}
                              </div>
                              <span className={styles.name}>{record.counterpartyName}</span>
                            </div>
                            <span className={styles.transactionId}>
                              {
                                // 交易记录编号
                                FI18n.i18nNext.tAuto('transactions_list_id')
                              } {record.id}
                            </span>
                          </div>
                        </div>
                        <div className={styles.tableCellInstruction}>
                          <div className={styles.transactionType}>{record.remark}</div>
                          <div style={{ height: 5 }} />
                          <FTooltip title={FI18n.i18nNext.tAuto('contract_list_btn_viewcontract')}>
                            <span>
                              <FComponentsLib.FTextBtn
                                type='default'
                                style={{ fontSize: 12 }}
                                onClick={() => {
                                  setState_FContractDetailsDrawer2({
                                    contractId: record.contractID,
                                    contractDetailShow: true,
                                  });
                                }}
                              >
                                {record.contractName}
                              </FComponentsLib.FTextBtn>
                            </span>
                          </FTooltip>
                        </div>
                        <div className={styles.tableCell}>
                          <div className={styles.amountInfo}>
                            <span className={styles.amountValue}>
                              {record.transactionAmount.toFixed(2)}
                            </span>
                            <span className={styles.currency}>CNY</span>
                          </div>
                        </div>
                        <div className={styles.tableCellStatus}>
                          <span className={styles.status}>
                            {getTransactionStatusText(record.transactionStatus)}
                          </span>
                        </div>
                      </div>
                    </React.Fragment>
                  ))}
                  <div className={styles.rowDivider}></div>
                </div>
              </div>
            )}
          </>
        </div>)
      }

      {$listState === 'loaded' && (
        <FListFooter state={$listMore} onClickLoadMore={() => handleListData(true)} />
      )}
      <FContractDetailsDrawer2 onClose={() => { }} />
    </div>
  );
}

export default connect(({ nodeManagerPage }: ConnectState) => ({
  nodeManagerPage,
}))(Transaction);

function getTransactionStatusText(status: number) {
  if (status === 1) {
    // 交易完成
    return FI18n.i18nNext.tAuto('tran_status_completed');
  }
  if (status === 2) {
    // 交易关闭
    return FI18n.i18nNext.tAuto('tran_status_closed');
  }
  // 系统处理中
  return FI18n.i18nNext.tAuto('tran_status_in_progress');
}
